import { FC } from "react";
import { PersonInformation } from "@/types";
import { useKeyPerson } from "@/hooks/useKeyPerson";
import { getFullName } from "../ComplexFields/PersonInformationComplexField";
import { SensitiveValueComponent } from "../SensitiveValueComponent/SensitiveValueComponent";
import moment from "moment";
import "./KeyPeople.scss";

export interface KeyPersonTableRowProps {
  financialApplicationId: string;
  keyPersonId: string;
  onDelete: (keyPersonId: string) => void;
  validationErrors: Record<string, string[]>;
  readOnly?: boolean;
  showAttorneyBarNumber?: boolean;
}

export const KeyPersonTableRow: FC<KeyPersonTableRowProps> = (props) => {
  const { formValue } = useKeyPerson(
    props.financialApplicationId,
    props.keyPersonId
  );

  if (!formValue) {
    return (
      <tr>
        <td colSpan={props.showAttorneyBarNumber ? 6 : 5}>Loading...</td>
      </tr>
    );
  }

  const personInfo = formValue.personInformation as PersonInformation;
  const fullName = getFullName(personInfo) || "New Key Person";
  
  // Get roles text
  const getRolesText = (): string => {
    const roles: string[] = [];
    
    if (formValue.isBeneficialOwner) {
      roles.push("Beneficial Owner");
    }
    if (formValue.isControlPerson) {
      roles.push("Control Person");
    }
    if (formValue.isAuthorizedSigner) {
      roles.push("Authorized Signer");
    }
    
    return roles.join(", ");
  };

  // Get date of birth
  const getDateOfBirth = (): string => {
    if (!personInfo?.dateOfBirth) return "";
    
    try {
      const date = moment(personInfo.dateOfBirth);
      if (!date.isValid()) return personInfo.dateOfBirth;

      // Format as M/D/YYYY
      return date.format("M/D/YYYY");
    } catch {
      return personInfo.dateOfBirth;
    }
  };

  // Get address text (truncated)
  const getAddressText = (): string => {
    if (!personInfo?.homeAddress?.street) return "";
    return personInfo.homeAddress.street.length > 15 
      ? `${personInfo.homeAddress.street.substring(0, 15)}...`
      : personInfo.homeAddress.street;
  };

  return (
    <tr className="key-people-table__row">
      <td className="key-people-table__cell key-people-table__cell--name">
        {fullName}
      </td>
      <td className="key-people-table__cell key-people-table__cell--role">
        {getRolesText()}
      </td>
      <td className="key-people-table__cell key-people-table__cell--dob">
        {getDateOfBirth()}
      </td>
      <td className="key-people-table__cell key-people-table__cell--ssn">
        {personInfo?.socialSecurityNumber && (
          <SensitiveValueComponent
            className="key-people-table__ssn-component"
          >
            {personInfo.socialSecurityNumber}
          </SensitiveValueComponent>
        )}
      </td>
      <td className="key-people-table__cell key-people-table__cell--address">
        {getAddressText()}
      </td>
      {props.showAttorneyBarNumber && (
        <td className="key-people-table__cell key-people-table__cell--attorney">
          {formValue.attorneyBarNumber || ""}
        </td>
      )}
    </tr>
  );
}; 